import {useCards} from '@/stores/cards/store';
import {useRouter} from '@/navigation';
import {useTranslations} from 'next-intl';
import z from 'zod';
import {useFormik} from 'formik';
import {toFormikValidationSchema} from 'zod-formik-adapter';
import React, {ChangeEvent, useEffect, useState} from 'react';
import CheckoutPaymentSelection from '@/app/[locale]/checkout/components/payment/CheckoutPaymentSelection';
import {Card, defaultCard} from '@/models/card';
import {loadStripe, Stripe} from '@stripe/stripe-js';
import InvoiceSection from '@/app/[locale]/giftcard/components/InvoiceSection';
import useSession from '@/stores/session/useSession';
import {PayPalButton} from '@/app/[locale]/checkout/components/review/PayPalButton';
import {Button, Input, Textarea, Modal} from '@/components/design-system';
import UtilitiesService from '@/services/utilities.service';
import logger from '@/logger';
import {BillingData} from '@winelivery-org/wl-client-sdk-js';

const schema = z.object({
  amount: z.coerce
    .number({required_error: 'form.errors.required'})
    .int()
    .gt(0, {message: 'form.errors.required'}),
  senderName: z
    .string({required_error: 'form.errors.required'})
    .max(50, {message: 'form.errors.max'}),
  recipientName: z
    .string({required_error: 'form.errors.required'})
    .max(50, {message: 'form.errors.max'}),
  email: z
    .string({required_error: 'form.errors.required'})
    .email({message: 'form.errors.email'})
    .max(100, {message: 'form.errors.max'}),
  bigliettino: z.string().max(50, {message: 'form.errors.max'}).optional(),
  paymentMethod: z.string({required_error: 'form.errors.required'}),
});

export default function () {
  const {user} = useSession();
  const prices = [25, 50, 100, 200];
  const paymentMethods = ['credit card', 'paypal'];
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isListVisible, setIsListVisible] = useState<boolean>(true);
  const [invoiceEnabled, setInvoiceEnabled] = useState<boolean>(false);
  const [invoiceFormVisible, setInvoiceFormVisible] = useState<boolean>(false);
  const [invoice, setInvoice] = useState<BillingData>({
    businessName: '',
    address: '',
    vat: '',
    socialSecurityNumber: '',
    pec: '',
  });

  const {fetchCards} = useCards();
  const router = useRouter();
  const tg = useTranslations('giftcard');
  const tc = useTranslations('common');
  const [card, setCard] = useState<Card>(defaultCard);
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const {
    handleChange,
    handleSubmit,
    isSubmitting,
    setSubmitting,
    isValid,
    dirty,
    touched,
    setFieldTouched,
    values: {
      amount,
      senderName,
      recipientName,
      email,
      bigliettino,
      paymentMethod,
    },
    errors,
  } = useFormik({
    validationSchema: toFormikValidationSchema(schema),
    initialValues: {
      amount: 0,
      senderName: '',
      recipientName: '',
      email: '',
      bigliettino: '',
      paymentMethod: '',
    },
    onSubmit: async () => {
      await purchaseGiftcard();
    },
  });

  const willMount = async () => {
    setStripe(
      (await loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY!)) as Stripe,
    );
    await fetchCards();
  };

  useEffect(() => {
    willMount().then(() => console.log('stripe and cards loaded.'));
  }, []);

  const invoiceCheckboxChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setInvoiceEnabled(event.target.checked);
    if (
      event.target.checked &&
      user!.billingData &&
      user!.billingData.length > 0
    ) {
      setInvoice(user!.billingData[0]);
    } else {
      setInvoice({
        businessName: '',
        address: '',
        vat: '',
        socialSecurityNumber: '',
        pec: '',
      });
    }
  };

  const handleCardAction = async (paymentIntentClientSecret: string) => {
    let stripeResponse;

    try {
      stripeResponse = await stripe!.handleCardAction(
        paymentIntentClientSecret,
      );
    } catch (err) {
      logger.error(err);
      return;
    }

    if (stripeResponse.error) {
      alert(tg('GiftcardForm.request-error'));
    } else {
      purchaseGiftcard(undefined, stripeResponse.paymentIntent.id);
    }
  };

  const purchaseGiftcard = async (
    orderID?: string,
    paymentIntentId?: string,
  ) => {
    try {
      const giftcard = {
        email,
        amount: Number(amount),
        payment: paymentMethod === 'credit card' ? 'stripe' : 'paypal',
        paymentIntentId,
        paymentMethodId: paymentIntentId ? undefined : card.token,
        orderID,
        senderName,
        recipientName,
        card: bigliettino,
        invoice: invoiceEnabled ? invoice || undefined : undefined,
      };

      setSubmitting(true);

      // @ts-ignore
      const response = await new UtilitiesService().purchaseGiftcard(giftcard);

      if (response.requires_action) {
        handleCardAction(response.payment_intent_client_secret);
      } else {
        setIsModalOpen(true);
      }
    } catch (err) {
      logger.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const onPaymentApproved = async ({orderID}, details) => {
    await purchaseGiftcard(orderID);
    logger.info(details);
  };

  return (
    <div className="tw-mt-6 tw-container tw-mx-auto">
      <form onSubmit={handleSubmit}>
        <div>
          <div className="tw-font-bold tw-mb-2 tw-text-center">
            {tg('GiftcardForm.amount')}
          </div>
          <div className="mx-auto tw-grid tw-grid-cols-2 md:tw-flex tw-gap-2 md:tw-mx-auto md:tw-w-fit md:tw-max-w-full max-md:-tw-mx-4">
            {prices.map((price, idx) => {
              const className =
                amount.toString() === price.toString()
                  ? 'tw-btn-primary'
                  : 'tw-btn-primary-o';
              return (
                <label
                  key={idx}
                  className="tw-block tw-basis-24 md:first:tw-pl-4 md:last:tw-pr-4"
                >
                  <input
                    className="tw-hidden"
                    type="radio"
                    name="amount"
                    value={price}
                    onChange={handleChange}
                  />
                  <div
                    className={`tw-button tw-btn-lg ${className} tw-w-full tw-text-xl`}
                  >
                    <span>{`€${price}`}</span>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
        <div className="tw-font-bold tw-mb-2 tw-mt-4 tw-text-center">
          {tg('GiftcardForm.info')}
        </div>
        <div className="tw-flex tw-flex-col tw-gap-4">
          <div>
            <Input
              label={tg('GiftcardForm.sender-name')}
              disabled={isSubmitting}
              value={senderName}
              placeholder={tg('GiftcardForm.insert-sender-name')}
              name="senderName"
              onChange={(e) => {
                handleChange(e);
                if (!touched.senderName) {
                  setFieldTouched('senderName', true, true);
                }
              }}
              {...(errors.senderName &&
                touched.senderName && {errorMessage: tc(errors.senderName)})}
            />
          </div>
          <div>
            <Input
              label={tg('GiftcardForm.recipient-name')}
              disabled={isSubmitting}
              value={recipientName}
              placeholder={tg('GiftcardForm.insert-recipient-name')}
              name="recipientName"
              onChange={(e) => {
                handleChange(e);
                if (!touched.recipientName) {
                  setFieldTouched('recipientName', true, true);
                }
              }}
              {...(errors.recipientName &&
                touched.recipientName && {
                  errorMessage: tc(errors.recipientName),
                })}
            />
          </div>
          <div>
            <Input
              label={tg('GiftcardForm.recipient-email')}
              disabled={isSubmitting}
              value={email}
              placeholder={tg('GiftcardForm.insert-email')}
              name="email"
              onChange={(e) => {
                handleChange(e);
                if (!touched.email) {
                  setFieldTouched('email', true, true);
                }
              }}
              {...(errors.email &&
                touched.email && {errorMessage: tc(errors.email)})}
            />
          </div>
          <div>
            <Textarea
              label={tg('GiftcardForm.message')}
              disabled={isSubmitting}
              value={bigliettino}
              placeholder={tg('GiftcardForm.insert-message')}
              name="bigliettino"
              onChange={(e) => {
                handleChange(e);
                if (!touched.bigliettino) {
                  setFieldTouched('bigliettino', true, true);
                }
              }}
              {...(errors.bigliettino &&
                touched.bigliettino && {errorMessage: tc(errors.bigliettino)})}
            />
          </div>

          <div>
            <div className="tw-mb-2 tw-font-bold tw-text-center">
              {tg('GiftcardForm.payment-method')}
            </div>
            <div>
              <div className="tw-flex tw-justify-center tw-gap-2 tw-flex-wrap">
                {paymentMethods.map((pm, idx) => {
                  const className =
                    paymentMethod === pm
                      ? 'tw-btn-primary'
                      : 'tw-btn-primary-o';

                  return (
                    <label key={idx}>
                      <input
                        className="tw-hidden"
                        type="radio"
                        name="paymentMethod"
                        value={pm}
                        onChange={handleChange}
                      />
                      <div
                        className={
                          'tw-button tw-btn-sm ' + className + ' tw-min-w-24'
                        }
                      >
                        <span>
                          {pm === 'credit card' ? 'kreditkarte' : pm}
                        </span>
                      </div>
                    </label>
                  );
                })}
              </div>

              {paymentMethod === 'credit card' && (
                <div className="tw-mt-4">
                  <CheckoutPaymentSelection
                    card={card}
                    setCheckoutCard={(newCard) => {
                      setCard(newCard);
                    }}
                    resetCheckoutCard={() => {
                      setCard(defaultCard);
                    }}
                    isListVisible={isListVisible}
                    setIsListVisible={setIsListVisible}
                  />
                </div>
              )}
            </div>
          </div>

          <InvoiceSection
            invoiceEnabled={invoiceEnabled}
            invoiceCheckboxChanged={invoiceCheckboxChanged}
            invoiceFormVisible={invoiceFormVisible}
            invoice={invoice}
            onSubmitted={(submittedInvoice) => {
              setInvoice(submittedInvoice);
              setInvoiceFormVisible(false);
            }}
            setIsInvoiceFormVisible={() =>
              setInvoiceFormVisible(!invoiceFormVisible)
            }
          />
          <hr className="tw-mt-4" />
          <div>
            {paymentMethod !== 'paypal' ||
            !dirty ||
            !isValid ||
            (invoiceEnabled && invoiceFormVisible) ? (
              <Button
                size="lg"
                className="tw-w-full tw-text-xl"
                type="submit"
                disabled={
                  isSubmitting ||
                  !dirty ||
                  !isValid ||
                  (invoiceEnabled && invoiceFormVisible)
                }
                loading={isSubmitting}
              >
                {tg('GiftcardForm.purchase-now')}
              </Button>
            ) : (
              <PayPalButton
                totalAmount={Number(amount!)}
                onPaymentApproved={onPaymentApproved}
                onPaymentApproving={() => setSubmitting(true)}
                onPaymentRejected={() => {
                  alert(tg('GiftcardForm.request-error'));
                  setSubmitting(false);
                }}
              />
            )}
          </div>
        </div>
      </form>
      <Modal
        whiteHeader
        size="sm"
        isOpen={isModalOpen}
        title={tg('GiftcardForm.giftcard-purchased')}
        onClose={() => {
          router.replace('/');
          setIsModalOpen(false);
        }}
      >
        <p className="tw-text-center">
          {tg('GiftcardForm.giftcard-purchased-successfully')}
        </p>
        <div className="tw-text-center">
          <Button
            onClick={() => {
              router.replace('/');
              setIsModalOpen(false);
            }}
          >
            OK
          </Button>
        </div>
      </Modal>
    </div>
  );
}
